/** Default bucket for employees with no salary company assigned. */
export const DEFAULT_SALARY_COMPANY_NAME = "The Coding Studio";
export const DEFAULT_SALARY_COMPANY_TAB_ID = "__the_coding_studio__";

const normalizeName = (value) => String(value || "").trim().toLowerCase();

export const isTheCodingStudioCompanyName = (name) =>
  normalizeName(name) === normalizeName(DEFAULT_SALARY_COMPANY_NAME);

export const findTheCodingStudioCompany = (companies = []) =>
  companies.find((company) => isTheCodingStudioCompanyName(company?.companyName)) || null;

export const getTheCodingStudioTabId = (companies = []) => {
  const match = findTheCodingStudioCompany(companies);
  return match ? String(match.id) : DEFAULT_SALARY_COMPANY_TAB_ID;
};

export const isTheCodingStudioTabActive = (activeTabId, companies = []) =>
  String(activeTabId) === getTheCodingStudioTabId(companies) ||
  String(activeTabId) === DEFAULT_SALARY_COMPANY_TAB_ID;

export const resolveAssignedSalaryCompanyId = (entry, salaryCompanyByUserId) => {
  const fromEntry = String(entry?.salaryCompanyId ?? "").trim();
  if (fromEntry) return fromEntry;
  const fromUser = String(salaryCompanyByUserId?.get(Number(entry?.userId)) || "").trim();
  return fromUser || null;
};

/** Salary company id to store when adding rows from the active finance tab. */
export const resolveSalaryCompanyIdForActiveTab = (activeTabId, companies = []) => {
  if (isTheCodingStudioTabActive(activeTabId, companies)) {
    const codingStudio = findTheCodingStudioCompany(companies);
    return codingStudio ? String(codingStudio.id) : null;
  }
  const tabId = String(activeTabId || "").trim();
  return tabId || null;
};

/** Employees with no company, or explicitly assigned to The Coding Studio. */
export const entryBelongsToTheCodingStudioTab = (entry, companies = [], salaryCompanyByUserId) => {
  const assignedId = resolveAssignedSalaryCompanyId(entry, salaryCompanyByUserId);
  if (!assignedId) return true;
  const codingStudio = findTheCodingStudioCompany(companies);
  return codingStudio ? String(assignedId) === String(codingStudio.id) : false;
};

/** Whether a sheet row / salary structure belongs on the active company tab. */
export const matchesActiveSalaryCompanyTab = (
  row,
  activeTabId,
  companies = [],
  salaryCompanyByUserId
) => {
  const tabId = String(activeTabId || "").trim();
  if (!tabId) return false;
  if (isTheCodingStudioTabActive(tabId, companies)) {
    return entryBelongsToTheCodingStudioTab(row, companies, salaryCompanyByUserId);
  }
  const assignedId = resolveAssignedSalaryCompanyId(row, salaryCompanyByUserId);
  return assignedId != null && String(assignedId) === tabId;
};

export const financeCompaniesForTabs = (companies = []) =>
  companies.filter((company) => !isTheCodingStudioCompanyName(company?.companyName));

export const shouldShowTheCodingStudioTab = (entries = [], companies = [], salaryCompanyByUserId) => {
  if (findTheCodingStudioCompany(companies)) return true;
  return entries.some((entry) => entryBelongsToTheCodingStudioTab(entry, companies, salaryCompanyByUserId));
};

export const slipBelongsToTheCodingStudioTab = (slip, companies = []) => {
  const assignedId = String(slip?.salaryCompanyId || "").trim();
  if (!assignedId) return true;
  const codingStudio = findTheCodingStudioCompany(companies);
  return codingStudio ? assignedId === String(codingStudio.id) : false;
};

/** Default salary company value for employee drawer (DB id if company exists, else empty). */
export const getDefaultSalaryCompanyOptionValue = (companies = []) => {
  const codingStudio = findTheCodingStudioCompany(companies);
  return codingStudio ? String(codingStudio.id) : "";
};

/** Salary company dropdown: default first, then other finance companies. */
export const buildFinanceSalaryCompanyDrawerOptions = (companies = []) => {
  const codingStudio = findTheCodingStudioCompany(companies);
  const defaultOption = {
    value: getDefaultSalaryCompanyOptionValue(companies),
    label: codingStudio
      ? `${codingStudio.companyName?.trim() || DEFAULT_SALARY_COMPANY_NAME} (default)`
      : `${DEFAULT_SALARY_COMPANY_NAME} (default)`,
  };

  const rest = companies
    .filter((profile) => !codingStudio || String(profile.id) !== String(codingStudio.id))
    .map((profile) => ({
      value: String(profile.id),
      label: profile.companyName?.trim() || `Company ${profile.id}`,
    }));

  return [defaultOption, ...rest];
};
